import { CommonModule } from '@angular/common'
import { Component, inject, OnInit } from '@angular/core'
import { Router, RouterOutlet, RoutesRecognized } from '@angular/router'
import { Store } from '@ngrx/store'
import { SimpleNotificationsModule } from 'angular2-notifications'
import type { KeycloakProfile } from 'keycloak-js'
import { MessageModule } from 'primeng/message'
import { catchError, filter, from, of, pairwise, Subscription } from 'rxjs'
import packageInfo from '../../package.json'
import { User, UserRoleEnum } from './shared/models/interfaces/user.interface'
import { AuthenticationService } from './shared/services/authentication.service'
import { ConfigService } from './shared/services/config.service'
import { LogService } from './shared/services/log.service'
import { RetryConnectionService } from './shared/services/retry-connection.service'
import { SpinnerService } from './shared/services/spinner/spinner.service'
import {
   setCountries,
   setMeasuringPurpose,
   setPathLocationPrevious,
   setPNOCountryResponsibles,
   setSectors,
   setStatuses,
   setTechnologies,
   setUser,
   setVisibilities,
} from './shared/store/stage/stage.actions'
import { getUser } from './shared/store/stage/stage.selectors'
import { getNumberOfYourDrafts } from './shared/store/feature-manage-your-drafts/feature-myd.actions'

@Component({
   selector: 'app-root',
   standalone: true,
   imports: [RouterOutlet, SimpleNotificationsModule, CommonModule, MessageModule],
   templateUrl: './app.component.html',
   styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
   private _cn = this.constructor.name
   private _store = inject(Store)
   private _router = inject(Router)
   private _logService = inject(LogService)
   private _configService = inject(ConfigService)
   private _spinnerService = inject(SpinnerService)
   private _authService = inject(AuthenticationService)

   protected retryConnectionService = inject(RetryConnectionService)

   private _user?: User
   private sub = new Subscription()

   constructor() {
      this._logService.packageName = packageInfo.name
      this._logService.packageVersion = packageInfo.version
      this._logService.enableLog = this._configService.environment != 'prod'
   }

   async ngOnInit(): Promise<void> {
      const mn = this.ngOnInit.name

      let userLogged: boolean = false

      await this._authService.userIsLogged().then((isLogged) => {
         userLogged = isLogged
      })

      if (!userLogged) return

      this._authService.hasLicence().then((hasLicence) => {
         if (hasLicence) {
            this.setUser()
            this._store.dispatch(setStatuses())
            this._store.dispatch(setVisibilities())
            this._store.dispatch(setMeasuringPurpose())
            this._store.dispatch(setSectors())
            this._store.dispatch(setTechnologies())
            this._store.dispatch(setCountries())
            this._store.dispatch(setPNOCountryResponsibles())
            this.initPreviuosRouteListner()
         } else {
            this.accessDenied()
         }
      })

      this.sub.add(
         this._authService.licenceSubject$.subscribe((hasLicence) => {
            if (!hasLicence) {
               if (!this._router.url.includes('access-denied')) {
                  this.accessDenied()
               }
            } else {
               this._authService.retriveRole().then((role) => {
                  if (this._router.url.includes('access-denied') || role != this._user?.role) {
                     window.location.reload()
                  }
               })
            }
         })
      )

      this.sub.add(this._store.select(getUser).subscribe((user) => (this._user = user)))
   }

   ngOnDestroy(): void {
      this.sub.unsubscribe()
   }

   private setUser() {
      const mn = this.setUser.name
      from(this._authService.keycloakAngular.loadUserProfile())
         .pipe(
            filter((res) => res != null && res != undefined),
            catchError((error) => {
               this._logService.error(this._cn, mn, 'Cannot retrive user info... logout')
               this._authService.keycloakAngular.logout()
               return of(error)
            })
         )
         .subscribe((user: KeycloakProfile) => {
            this._authService.retriveRole().then((role) => {
               this._store.dispatch(setUser({ user: { name: `${user.firstName} ${user.lastName}`, role: role } }))
               if (role == UserRoleEnum.ADMIN) this._store.dispatch(getNumberOfYourDrafts())
            })
         })
   }

   private accessDenied() {
      const mn = this.accessDenied.name
      this._logService.error(this._cn, mn, 'user does not have licence...')
      this._router.navigate(['/access-denied'])
   }

   private initPreviuosRouteListner() {
      this.sub.add(
         this._router.events
            .pipe(
               filter((evt: any) => evt instanceof RoutesRecognized),
               pairwise()
            )
            .subscribe((events: RoutesRecognized[]) => {
               this._store.dispatch(setPathLocationPrevious({ pathLocationPrevious: events[0].urlAfterRedirects }))
            })
      )
   }
}
